/**
 * Editor de cotización (nueva / editar): cliente, buscador de productos, líneas con triple precio del servidor, vigencia.
 * Cada guardado = versión. Los combos y recetas (spec 20) entran como bloque, igual que en el POS: cambiar la medida los
 * recalcula con su descuento; quitar un producto o desarmarlos los deja sueltos y sin el descuento del combo.
 */
import { formatearTasa, Money, type CalculoComboDTO, type CalculoCotizacionDTO, type ClienteDTO, type ProductoResumenDTO } from '@mg/shared';
import { Layers, Lock, Save, Search, Trash2 } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { Dinero } from '@/componentes/Dinero.tsx';
import { PaginadorBusqueda, usePaginaBusqueda } from '@/componentes/PaginadorBusqueda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Textarea } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError, nuevaClaveOperacion } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { veTasa2 } from '@/lib/tasa2.ts';
import { useClientes } from '@/modulos/clientes/api.ts';
import { combosParaEnviar, desarmarCombo, esPrimeroDeSuCombo, fotoDeCalculo, quitarCombo, reemplazarCombo, textoMedida, type ComboEnCarrito } from '@/modulos/combos/carrito.ts';
import { DialogAgregarCombo } from '@/modulos/combos/DialogAgregarCombo.tsx';
import { useProductos } from '@/modulos/productos/api.ts';
import { calcularCotizacion, useCotizacion, useCrearCotizacion, useEditarCotizacion } from './api.ts';
import { normalizarDecimal, parseMonto } from '@/lib/montos.ts';

/** Productos por página en el buscador de la cotización. */
const LIMITE_COT = 12;

interface Linea {
  producto: ProductoResumenDTO;
  presentacion_id: string;
  cantidad: string;
  /** Descuento % que el renglón ya traía en la cotización: viaja de vuelta para que editar no lo pierda. */
  descuento_pct?: string;
  /** Combo o receta del renglón (spec 20): mientras lo tenga lleva el descuento del combo y su cantidad no se edita suelta. */
  combo_grupo?: string;
}

export function EditorCotizacion() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { puede, usuario } = useAuth();
  // La tasa 2 del cálculo solo la ve la dueña (pedido de la dueña 2026-10-19)
  const veTasa = veTasa2(usuario);
  const existente = useCotizacion(id ?? null);
  const crear = useCrearCotizacion();
  const editar = useEditarCotizacion(id ?? '');
  const buscador = useRef<HTMLInputElement>(null);
  const [cliente, setCliente] = useState<ClienteDTO | null>(null);
  const [qCliente, setQCliente] = useState('');
  // Igual que el POS: al enfocar el campo se despliega la lista de clientes sin tener que escribir
  const [listaClientes, setListaClientes] = useState(false);
  const [q, setQ] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([]);
  /** Combos y recetas de la cotización (spec 20). */
  const [combos, setCombos] = useState<ComboEnCarrito[]>([]);
  /** Ventana «Agregar combo o receta» (`grupo` = «Cambiar medida» de uno que ya está). */
  const [dialogoCombo, setDialogoCombo] = useState<{ grupo: string | null; inicial: Parameters<typeof DialogAgregarCombo>[0]['inicial'] } | null>(null);
  /** Confirmación de desarmar un combo (`linea`: el renglón que se quita al desarmar; null = solo desarmar). */
  const [desarme, setDesarme] = useState<{ grupo: string; nombre: string; linea: number | null } | null>(null);
  const [descuento, setDescuento] = useState('');
  const [vigencia, setVigencia] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [calculo, setCalculo] = useState<CalculoCotizacionDTO | null>(null);
  const [errorCalculo, setErrorCalculo] = useState<string | null>(null);
  const [hidratada, setHidratada] = useState(false);
  const puedeDescontar = puede('ventas.descuento');
  const clientes = useClientes({ q: qCliente || undefined, limite: 8 }, qCliente.length > 0 || listaClientes);
  // De 12 en 12 con paginador al pie, como el POS (pedido de la dueña 2026-10-20)
  const [paginaProd, setPaginaProd] = usePaginaBusqueda(`${q}|${cliente?.lista_id ?? ''}`);
  const productos = useProductos({ q: q || undefined, lista_id: cliente?.lista_id, pagina: paginaProd, limite: LIMITE_COT, activo: 'true' });
  const listaProductos = useRef<HTMLDivElement>(null);

  // Nueva cotización: arranca con Consumidor Final (lista detal) como el POS, así los precios se ven de inmediato; el usuario puede cambiarlo
  useEffect(() => {
    if (id || cliente) return;
    let vivo = true;
    get<{ datos: ClienteDTO[] }>('/clientes', { q: 'V-00000000-0', limite: 1 })
      .then((r) => {
        if (vivo && r.datos[0]) setCliente(r.datos[0]);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [id, cliente]);

  // Edición: rehidrata cliente y líneas con la ficha actual de cada producto
  useEffect(() => {
    const c = existente.data;
    if (!id || !c || hidratada) return;
    const ids = [...new Set(c.lineas.map((l) => l.producto_id))];
    Promise.all([get<ClienteDTO>(`/clientes/${c.cliente_id}`), ...ids.map((pid) => get<ProductoResumenDTO>(`/productos/${pid}`))])
      .then(([cli, ...prods]) => {
        setCliente(cli as ClienteDTO);
        // Los renglones de un combo vuelven al bloque con su grupo y sin descuento manual: el del combo lo pone el
        // servidor. Si a un combo le falta un renglón (un producto que ya no carga), entra desarmado
        const cargadas = c.lineas.map((l) => ({ l, producto: (prods as ProductoResumenDTO[]).find((p) => p.id === l.producto_id) }));
        const incompletos = new Set(cargadas.filter((x) => !x.producto && x.l.combo_grupo).map((x) => x.l.combo_grupo));
        const enCombo = (g: string | null): g is string => !!g && !incompletos.has(g) && c.combos.some((k) => k.grupo === g);
        setLineas(
          cargadas.flatMap(({ l, producto }): Linea[] => {
            if (!producto) return [];
            if (enCombo(l.combo_grupo)) return [{ producto, presentacion_id: l.presentacion_id, cantidad: l.cantidad, combo_grupo: l.combo_grupo }];
            return [{ producto, presentacion_id: l.presentacion_id, cantidad: l.cantidad, ...(Money.parse(l.descuento_pct).esPositivo() && !l.descuento_combo ? { descuento_pct: l.descuento_pct } : {}) }];
          }),
        );
        setCombos(c.combos.filter((k) => enCombo(k.grupo)));
        setDescuento(Money.parse(c.descuento_pct).esPositivo() ? c.descuento_pct : '');
        setVigencia(String(c.vigencia_dias));
        setObservaciones(c.observaciones ?? '');
        setHidratada(true);
      })
      .catch((e) => toast.error('No se pudo cargar la cotización', { description: mensajeDeError(e) }));
  }, [id, existente.data, hidratada]);

  const entrada = useMemo(() => {
    if (!cliente || lineas.length === 0) return null;
    const enviarCombos = combosParaEnviar(combos, lineas);
    return {
      cliente_id: cliente.id,
      // Al editar, los descuentos viajan siempre de forma explícita (0 = quitado): lo que el servidor guarda es lo que
      // se ve aquí. Sin permiso de descuentos no se escribe ninguno: solo vuelve el que la cotización ya traía. Un
      // renglón de combo no lleva descuento manual: el del combo lo pone el servidor al validar la receta (spec 20)
      lineas: lineas.map((l) => ({
        producto_id: l.producto.id,
        presentacion_id: l.presentacion_id,
        cantidad: normalizarDecimal(l.cantidad) || '1',
        ...(l.descuento_pct && !l.combo_grupo ? { descuento_pct: l.descuento_pct } : id ? { descuento_pct: '0' } : {}),
        ...(l.combo_grupo ? { combo_grupo: l.combo_grupo } : {}),
      })),
      ...(enviarCombos.length > 0 ? { combos: enviarCombos } : {}),
      ...(descuento.trim() && (puedeDescontar || !!id) ? { descuento_pct: descuento.trim() } : id ? { descuento_pct: '0' } : {}),
    };
  }, [cliente, lineas, combos, descuento, puedeDescontar, id]);

  // Precios SIEMPRE del servidor (debounce)
  useEffect(() => {
    if (!entrada) {
      const t = setTimeout(() => { setCalculo(null); setErrorCalculo(null); }, 0);
      return () => clearTimeout(t);
    }
    let vivo = true;
    const t = setTimeout(() => {
      calcularCotizacion(entrada)
        .then((r) => { if (vivo) { setCalculo(r); setErrorCalculo(null); } })
        .catch((e) => { if (vivo) setErrorCalculo(mensajeDeError(e)); });
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [entrada]);

  const agregar = (p: ProductoResumenDTO, presentacionId?: string) => {
    const pres = presentacionId ?? p.presentaciones.find((x) => Money.parse(x.unidades).eq(1))?.id ?? p.presentaciones[0]?.id;
    if (!pres) { toast.error(`${p.codigo} no tiene presentaciones activas`); return; }
    setLineas((ls) => {
      const i = ls.findIndex((l) => l.producto.id === p.id && l.presentacion_id === pres && !l.combo_grupo);
      if (i >= 0) return ls.map((l, j) => (j === i ? { ...l, cantidad: (parseMonto(l.cantidad) ?? Money.cero()).add(1).toString() } : l));
      return [...ls, { producto: p, presentacion_id: pres, cantidad: '1' }];
    });
    setQ('');
    buscador.current?.focus();
  };

  /** Pone los renglones de un combo calculado, o cambia los de uno que ya está (otra medida: conserva el descuento). */
  const agregarCombo = async (calc: CalculoComboDTO, grupoExistente: string | null) => {
    try {
      const ids = [...new Set(calc.renglones.map((r) => r.producto_id))];
      const fichas = await Promise.all(ids.map((pid) => get<ProductoResumenDTO>(`/productos/${pid}`)));
      const grupo = grupoExistente ?? nuevaClaveOperacion();
      const nuevas: Linea[] = calc.renglones.map((r) => ({ producto: fichas.find((x) => x.id === r.producto_id)!, presentacion_id: r.presentacion_id, cantidad: Money.parse(r.cantidad).toString(), combo_grupo: grupo }));
      const foto = fotoDeCalculo(calc, grupo);
      setLineas((ls) => reemplazarCombo(ls, grupo, nuevas));
      setCombos((k) => [...k.filter((x) => x.grupo !== grupo), foto]);
      setDialogoCombo(null);
      toast.success(grupoExistente ? `Medida cambiada: ${calc.nombre}` : `${calc.nombre} en la cotización`, { description: `${textoMedida(foto)} · ${nuevas.length} producto(s) con el descuento del combo` });
      buscador.current?.focus();
    } catch (e) {
      toast.error('No se pudo agregar el combo', { description: mensajeDeError(e) });
    }
  };
  /** Quitar un renglón de un combo lo desarma: primero se confirma. */
  const quitarLinea = (i: number) => {
    const grupo = lineas[i]?.combo_grupo;
    if (grupo) {
      setDesarme({ grupo, nombre: combos.find((k) => k.grupo === grupo)?.nombre ?? 'el combo', linea: i });
      return;
    }
    setLineas((ls) => ls.filter((_, j) => j !== i));
  };
  /** Desarma el combo (sus renglones quedan sueltos, sin su descuento) y, si se pidió, quita el renglón. */
  const confirmarDesarme = () => {
    if (!desarme) return;
    const sueltos = desarmarCombo(lineas, desarme.grupo);
    setLineas(desarme.linea === null ? sueltos : sueltos.filter((_, j) => j !== desarme.linea));
    setCombos((k) => k.filter((x) => x.grupo !== desarme.grupo));
    setDesarme(null);
    toast.info('Combo desarmado', { description: 'Sus productos quedan sueltos, sin el descuento del combo.' });
  };
  const quitarComboEntero = (grupo: string) => {
    setLineas((ls) => quitarCombo(ls, grupo));
    setCombos((k) => k.filter((x) => x.grupo !== grupo));
  };

  const listo = !!entrada && !!calculo && !errorCalculo && calculo.lineas.every((l) => l.estado === 'ok');
  const guardar = () => {
    if (!entrada) return;
    const datos = { ...entrada, ...(vigencia.trim() ? { vigencia_dias: Number(vigencia) } : {}), ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}) };
    const m = id ? editar : crear;
    m.mutate(datos, {
      onSuccess: (c) => { toast.success(`Cotización ${c.numero_formateado} ${id ? `actualizada (v${c.version})` : 'creada'}`); navigate(`/cotizaciones/${c.id}`); },
      onError: (e) => toast.error('No se pudo guardar', { description: mensajeDeError(e) }),
    });
  };
  const doc = calculo ? { tasa_bcv: calculo.tasas.tasa_bcv, tasa_paralela: calculo.tasas.tasa_paralela, factor_divisa: calculo.tasas.factor_divisa } : null;

  return (
    <>
      <TituloPagina titulo={id ? `Editar ${existente.data?.numero_formateado ?? 'cotización'}` : 'Nueva cotización'} subtitulo="Triple precio (Bs · $ a BCV · $) con y sin IVA. Precios y tasa se congelan al guardar; cada guardado crea una versión." />
      <div className="grid gap-4 lg:grid-cols-[1fr_1.1fr]">
        <section className="space-y-2">
          <div className="relative">
            <Input
              placeholder="Cliente: nombre o RIF…"
              value={qCliente}
              onChange={(e) => setQCliente(e.target.value)}
              onFocus={() => setListaClientes(true)}
              onBlur={() => setTimeout(() => setListaClientes(false), 150)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setListaClientes(false); setQCliente(''); } }}
              className="h-9"
              data-testid="cot-cliente-buscar"
            />
            {(qCliente || listaClientes) && clientes.data && !clientes.isPlaceholderData ? (
              <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border bg-popover shadow-md" data-testid="cot-cliente-lista">
                {clientes.data.datos.map((c) => (
                  <li key={c.id}><button className="flex w-full flex-col px-3 py-1.5 text-left text-sm hover:bg-accent" onMouseDown={(e) => e.preventDefault()} onClick={() => { setCliente(c); setQCliente(''); setListaClientes(false); buscador.current?.focus(); }} data-testid={`cot-cliente-${c.rif}`}><span className="font-medium">{c.razon_social}</span><span className="text-[11px] text-muted-foreground">{c.rif} · {c.lista_nombre}</span></button></li>
                ))}
                {clientes.data.datos.length === 0 ? <li className="px-3 py-2 text-xs text-muted-foreground">Sin coincidencias.</li> : null}
              </ul>
            ) : null}
          </div>
          {cliente ? <p className="text-sm" data-testid="cot-cliente-actual"><b>{cliente.razon_social}</b> · {cliente.rif} · lista {cliente.lista_nombre}</p> : <p className="text-sm text-muted-foreground">Elige el cliente para ver sus precios de lista.</p>}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input ref={buscador} placeholder="Buscar producto (código, nombre, marca)… Enter agrega" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && productos.data?.datos[0]) agregar(productos.data.datos[0]); }} className="h-9 pl-9" disabled={!cliente} data-testid="cot-buscar" />
            </div>
            <Button type="button" variant="outline" className="h-9" disabled={!cliente} onClick={() => setDialogoCombo({ grupo: null, inicial: null })} title="Agregar un combo o una receta (varios productos de una vez)" data-testid="cot-combos"><Layers className="h-4 w-4" /> Combo o receta</Button>
          </div>
          <div className="overflow-hidden rounded-2xl border bg-card">
            <div ref={listaProductos} className="max-h-[52vh] overflow-y-auto" data-testid="cot-lista">
              {(productos.data?.datos.length ?? 0) === 0 ? <p className="p-4 text-sm text-muted-foreground">{cliente ? 'Sin resultados.' : 'Primero elige el cliente.'}</p> : (
                <ul className="divide-y">
                  {productos.data!.datos.map((p) => (
                    <li key={p.id} className="flex items-center gap-3 px-3 py-2" data-testid={`cot-producto-${p.codigo}`}>
                      <div className="min-w-0 flex-1"><p className="truncate font-medium">{p.nombre}</p><p className="text-[11px] text-muted-foreground">{p.codigo} · disp. <b className="num">{p.disponible_sucursal ?? '—'}</b></p></div>
                      <div className="flex flex-wrap justify-end gap-1">
                        {p.presentaciones.map((pr) => {
                          const precio = pr.precios.find((x) => x.lista_id === cliente?.lista_id)?.precio_unitario_divisa;
                          return <Button key={pr.id} size="sm" variant="outline" className="h-9 flex-col items-end gap-0 px-2 leading-tight" onClick={() => agregar(p, pr.id)} data-testid={`cot-agregar-${p.codigo}-${pr.unidades}`}><span className="text-[11px]">{pr.nombre}</span><span className="num text-[11px] text-muted-foreground">{precio ? `$ ${Money.parse(precio).toDisplay()} c/u` : 'sin precio'}</span></Button>;
                        })}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {cliente ? (
              <PaginadorBusqueda
                pagina={paginaProd}
                limite={LIMITE_COT}
                total={productos.data?.total ?? 0}
                onPagina={(n) => {
                  setPaginaProd(n);
                  if (listaProductos.current) listaProductos.current.scrollTop = 0;
                }}
                testid="cot-productos"
              />
            ) : null}
          </div>
        </section>

        <section className="flex flex-col rounded-2xl border bg-card">
          <div className="min-h-[200px] flex-1 overflow-y-auto">
            {lineas.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">Agrega productos desde el buscador.</p> : (
              <table className="w-full text-[13px]">
                <thead className="bg-muted/80 text-[11px] uppercase text-muted-foreground"><tr><th className="px-3 py-1.5 text-left">Producto</th><th className="px-2 py-1.5 text-right">Cant.</th><th className="px-2 py-1.5 text-right">Precio (Bs · $ a BCV · $)</th><th className="px-2 py-1.5 text-right">Subtotal (Bs · $ a BCV · $)</th><th /></tr></thead>
                <tbody>
                  {lineas.map((l, i) => {
                    const calc = calculo?.lineas[i];
                    const pres = l.producto.presentaciones.find((p) => p.id === l.presentacion_id);
                    const combo = l.combo_grupo ? combos.find((k) => k.grupo === l.combo_grupo) : undefined;
                    return (
                      <Fragment key={`${l.producto.id}-${l.presentacion_id}-${l.combo_grupo ?? ''}`}>
                        {/* Cabecera del bloque del combo o receta (spec 20) */}
                        {esPrimeroDeSuCombo(lineas, i) ? (
                          <tr className="border-t bg-mg-verde-claro/60" data-testid={`cot-combo-${combo?.codigo ?? ''}`}>
                            <td colSpan={5} className="px-3 py-1.5">
                              <div className="flex flex-wrap items-center gap-2 text-xs">
                                <Layers className="h-4 w-4 text-mg-verde" />
                                <span className="font-semibold text-foreground">{combo?.nombre ?? 'Combo'}</span>
                                {combo ? <span className="text-muted-foreground" data-testid="cot-combo-medida">{textoMedida(combo)}</span> : null}
                                <span className="ml-auto flex flex-wrap gap-1">
                                  {combo ? <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => setDialogoCombo({ grupo: combo.grupo, inicial: { combo_id: combo.combo_id, nombre: combo.nombre, tipo: combo.tipo, unidad_medida: combo.unidad_medida, medida: combo.medida } })} data-testid="cot-combo-cambiar">Cambiar medida</Button> : null}
                                  <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => setDesarme({ grupo: l.combo_grupo!, nombre: combo?.nombre ?? 'el combo', linea: null })} data-testid="cot-combo-desarmar">Desarmar</Button>
                                  <Button type="button" size="sm" variant="ghost" className="h-7 text-mg-rojo" onClick={() => quitarComboEntero(l.combo_grupo!)} data-testid="cot-combo-quitar">Quitar combo</Button>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                        <tr className={`border-t ${calc && calc.estado !== 'ok' ? 'bg-red-50' : l.combo_grupo ? 'bg-mg-verde-claro/20' : ''}`} data-testid={`cot-linea-${l.producto.codigo}`}>
                          <td className="px-3 py-1.5"><p className="font-medium">{l.producto.nombre} {calc && calc.estado !== 'ok' ? <Badge variant="critico">{calc.estado === 'inactivo' ? 'inactivo' : 'sin precio'}</Badge> : null}</p><p className="text-[11px] text-muted-foreground">{pres?.nombre} × {pres ? Money.parse(pres.unidades).toDisplay() : ''} u</p>
                            {/* Descuento del combo: solo mientras el renglón esté en el combo (spec 20) */}
                            {calc?.descuento_combo ? <p className="text-[11px] font-medium text-mg-verde" data-testid={`cot-descuento-combo-${l.producto.codigo}`}>−{Money.parse(calc.descuento_combo).toDisplay()} % · descuento del combo</p> : null}
                            {/* Descuento del renglón (programado por la dueña o el que la cotización ya traía): a la vista, para que el total cuadre */}
                            {calc && !calc.descuento_combo && Money.parse(calc.descuento_pct).esPositivo() ? (
                              <p className="text-[11px] font-medium text-amber-800" data-testid={`cot-linea-descuento-${l.producto.codigo}`}>
                                −{Money.parse(calc.descuento_pct).toDisplay()} % de descuento{' '}
                                {l.descuento_pct ? <button type="button" className="underline" onClick={() => setLineas((ls) => ls.map((x, j) => { if (j !== i) return x; const { descuento_pct: _d, ...resto } = x; return resto; }))} data-testid={`cot-linea-quitar-descuento-${l.producto.codigo}`}>quitar</button> : null}
                              </p>
                            ) : null}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            {l.combo_grupo ? (
                              <span className="inline-flex items-center gap-1 num" title="Para cambiar la cantidad, cambia la medida del combo o desármalo" data-testid={`cot-cantidad-${l.producto.codigo}`}>
                                <Lock className="h-3 w-3 text-muted-foreground" />
                                {Money.parse(normalizarDecimal(l.cantidad) || '0').toDisplay()}
                              </span>
                            ) : (
                              <Input inputMode="decimal" value={l.cantidad} onChange={(e) => setLineas((ls) => ls.map((x, j) => (j === i ? { ...x, cantidad: e.target.value } : x)))} className="h-8 w-16 text-right" aria-label={`Cantidad ${l.producto.codigo}`} data-testid={`cot-cantidad-${l.producto.codigo}`} />
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-right">{calc && doc ? <Dinero valor={calc.precio_unitario} modo="triple" doc={doc} bs={calc.precio_unitario_bs} /> : '…'}</td>
                          <td className="px-2 py-1.5 text-right">{calc && doc ? <Dinero valor={calc.subtotal} modo="triple" doc={doc} bs={calc.subtotal_bs} /> : '…'}</td>
                          <td className="px-1"><Button size="icon" variant="ghost" onClick={() => quitarLinea(i)} aria-label="Quitar" data-testid={`cot-quitar-${l.producto.codigo}`}><Trash2 className="h-4 w-4" /></Button></td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="space-y-2 border-t p-3" data-testid="cot-totales">
            {errorCalculo ? <p className="rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-800">{errorCalculo}</p> : null}
            <div className="grid grid-cols-3 gap-2">
              <Campo etiqueta="Vigencia (días · 0 = sin vencer)" htmlFor="cot-vigencia"><Input id="cot-vigencia" inputMode="numeric" placeholder="Sin vencimiento" value={vigencia} onChange={(e) => setVigencia(e.target.value)} className="h-8" data-testid="cot-vigencia" /></Campo>
              {puedeDescontar ? <Campo etiqueta="Descuento %" htmlFor="cot-desc"><Input id="cot-desc" inputMode="decimal" value={descuento} onChange={(e) => setDescuento(e.target.value)} className="h-8" data-testid="cot-descuento" /></Campo> : descuento.trim() && id ? (
                // Sin permiso de descuentos: el que la cotización ya trae se ve y se conserva al guardar; no se puede subir
                <div className="text-xs text-amber-900" role="status" data-testid="cot-descuento-conservado">
                  <p className="font-medium">Descuento {Money.parse(descuento).toDisplay()} %</p>
                  <p>Lo trae la cotización y se conserva. <button type="button" className="underline" onClick={() => setDescuento('')} data-testid="cot-descuento-quitar">Quitarlo</button></p>
                </div>
              ) : <div />}
              <Campo etiqueta="Observaciones" htmlFor="cot-obs"><Textarea id="cot-obs" rows={1} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} data-testid="cot-observaciones" /></Campo>
            </div>
            {calculo && doc ? (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="space-y-0.5 text-xs text-muted-foreground">
                  <div className="flex items-start justify-between gap-2"><span>Sin IVA</span><Dinero valor={calculo.totales.base} modo="triple" doc={doc} bs={calculo.totales.base_bs} /></div>
                  <p>IVA {calculo.totales.iva_pct}% <b className="num text-foreground">$ {Money.parse(calculo.totales.iva).toDisplay()}</b>{Money.parse(calculo.totales.descuento).esPositivo() ? <> · desc. $ {Money.parse(calculo.totales.descuento).toDisplay()}</> : null}</p>
                  <p>{veTasa ? <>Tasa 2 {formatearTasa(calculo.totales.tasa_paralela)} · </> : null}BCV {formatearTasa(calculo.totales.tasa_bcv)}</p>
                </div>
                <div className="text-right"><p className="text-xs text-muted-foreground">Total con IVA</p><Dinero valor={calculo.totales.total} modo="triple" doc={doc} bs={calculo.totales.total_bs} className="text-base" /></div>
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate(id ? `/cotizaciones/${id}` : '/cotizaciones')}>Cancelar</Button>
              <Button disabled={!listo} cargando={crear.isPending || editar.isPending} onClick={guardar} data-testid="cot-guardar"><Save /> {id ? 'Guardar nueva versión' : 'Guardar cotización'}</Button>
            </div>
          </div>
        </section>
      </div>

      {/* Combos y recetas (spec 20): agregar o cambiar la medida, y confirmar antes de desarmar */}
      <DialogAgregarCombo abierto={!!dialogoCombo} cerrar={() => setDialogoCombo(null)} inicial={dialogoCombo?.inicial ?? null} alAgregar={(c) => void agregarCombo(c, dialogoCombo?.grupo ?? null)} textoAgregar="Agregar a la cotización" />
      <Dialog open={!!desarme} onOpenChange={(o) => !o && setDesarme(null)}>
        <DialogContent className="max-w-md" data-testid="dialogo-desarmar-combo">
          <DialogHeader>
            <DialogTitle>¿Desarmar {desarme?.nombre}?</DialogTitle>
            <DialogDescription>
              {desarme?.linea === null ? 'Sus productos quedan en la cotización como renglones sueltos' : 'Se quita ese producto y los demás quedan como renglones sueltos'}, sin el descuento del combo. El descuento solo vale si se cotiza el combo completo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDesarme(null)}>Cancelar</Button>
            <Button onClick={confirmarDesarme} data-testid="confirmar-desarmar-combo">{desarme?.linea === null ? 'Desarmar' : 'Quitar y desarmar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
